import React, { useMemo } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { navSections } from 'src/_nav'

// Strips a page-type suffix (_list, _create, _detail, _unresolved, _profile)
// off a URL's third path segment (the "resource segment") so that sibling
// pages of the SAME resource — e.g. contact_cases_detail/:id and
// contact_cases_unresolved, or contacts_list and contacts_detail/:id — key
// to the same resource identity even though their literal segment text
// differs.
function stripPageTypeSuffix(segment) {
  return (segment || '').replace(/_(list|create|detail|unresolved|profile)$/, '')
}

// Builds the resource-identity key for a pathname/URL: folder + the
// resource segment with its page-type suffix stripped.
function getResourceKey(pathname) {
  const parts = pathname.split('/').filter(Boolean) // e.g. ['resources','contacts','contact_cases_detail','id']
  if (parts[0] !== 'resources' || parts.length < 2) return null
  const folder = parts[1]
  const resourceSegment = stripPageTypeSuffix(parts[2] || '')
  return `/resources/${folder}/${resourceSegment}`
}

// Extracts the /resources/<folder>/ prefix from a URL, or null if it isn't
// a /resources/... URL.
function getFolderPrefix(pathname) {
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0] !== 'resources' || parts.length < 2) return null
  return `/resources/${parts[1]}/`
}

/**
 * Build a lookup map from resource keys / folder prefixes to
 * { sectionName, itemName, itemTo }. This lets us resolve any current URL
 * to its breadcrumb trail using navSections.
 *
 * Most /resources/<folder>/ folders hold exactly one nav item, and pages
 * inside that folder (list/detail/create/... plus differently-named
 * sub-resource pages, e.g. webchat_widgets/sessions_detail) all belong to
 * it — so those items still match by folder prefix, same as before.
 *
 * A few folders (currently just contacts/) hold MULTIPLE distinct nav
 * items — e.g. Contacts (contacts_list) and Cases (contact_cases_*) both
 * live under /resources/contacts/. For those, folder-prefix matching can't
 * tell them apart (whichever item is registered first always wins), so
 * items in a shared folder are matched by resource key instead (folder +
 * page-type-suffix-stripped resource segment) to disambiguate siblings.
 */
function buildNavLookup() {
  const lookup = []
  const folderCounts = {}

  Object.values(navSections).forEach((section) => {
    section.items.forEach((item) => {
      const folderPrefix = getFolderPrefix(item.to)
      if (folderPrefix) {
        folderCounts[folderPrefix] = (folderCounts[folderPrefix] || 0) + 1
      }
    })
  })

  Object.values(navSections).forEach((section) => {
    section.items.forEach((item) => {
      const folderPrefix = getFolderPrefix(item.to)
      const sharedFolder = folderPrefix && folderCounts[folderPrefix] > 1

      lookup.push({
        folderPrefix,
        sharedFolder,
        resourceKey: sharedFolder ? getResourceKey(item.to) : null,
        exactTo: item.to,
        sectionName: section.name,
        sectionDashboard: section.items.find((i) => i.to.includes('/dashboard'))?.to,
        itemName: item.name,
        itemTo: item.to,
      })
    })
  })

  return lookup
}

const navLookup = buildNavLookup()

function resolveBreadcrumbs(pathname) {
  const crumbs = [{ label: null, to: '/dashboard', isHome: true }]

  // Root or main dashboard
  if (pathname === '/' || pathname === '/dashboard') {
    return crumbs
  }

  // Settings page
  if (pathname === '/settings') {
    crumbs.push({ label: 'Settings', to: null })
    return crumbs
  }

  // Find matching nav item, in two passes:
  //   1) Exact resource-key match among shared-folder items (disambiguates
  //      siblings like Cases vs Contacts under /resources/contacts/).
  //   2) Folder-prefix fallback across ALL items (including shared-folder
  //      ones) for any URL that didn't hit an exact resource-key match —
  //      e.g. /resources/contacts/contact_addresses_detail/:id is a THIRD,
  //      non-nav resource living in the shared contacts/ folder; it has no
  //      resourceKey match against Contacts or Cases, so it must still fall
  //      back to whichever shared-folder item's folder prefix matches
  //      (Contacts, being registered first) rather than resolve to nothing.
  const pathnameResourceKey = getResourceKey(pathname)
  const exactMatch = navLookup.find(
    (entry) => entry.sharedFolder && pathnameResourceKey !== null && pathnameResourceKey === entry.resourceKey
  )
  const match = exactMatch || navLookup.find((entry) => entry.folderPrefix && pathname.startsWith(entry.folderPrefix))
    || navLookup.find((entry) => !entry.folderPrefix && (pathname === entry.itemTo || pathname.startsWith(entry.itemTo + '/')))

  if (!match) {
    // Fallback: show the last path segment as a label
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length > 0) {
      crumbs.push({ label: segments[segments.length - 1], to: null })
    }
    return crumbs
  }

  // Add section crumb
  const sectionTo = match.sectionDashboard || null
  const isOnSectionDashboard = pathname === match.sectionDashboard
  crumbs.push({
    label: formatSectionName(match.sectionName),
    to: isOnSectionDashboard ? null : sectionTo,
  })

  // If we're on a section dashboard, stop here
  if (isOnSectionDashboard) {
    return crumbs
  }

  // Add item crumb (e.g., "Calls", "Agents")
  // If we're on the exact item page (the list), it's the last crumb
  const isOnItemPage = pathname === match.itemTo
  crumbs.push({
    label: match.itemName,
    to: isOnItemPage ? null : match.itemTo,
  })

  // If deeper (detail/create), add the page type
  if (!isOnItemPage) {
    const pageType = getPageType(pathname)
    if (pageType) {
      crumbs.push({ label: pageType, to: null })
    }
  }

  return crumbs
}

function formatSectionName(name) {
  // navSections names are already in Title Case (e.g. "Voice", "AI Services").
  // Preserve the all-caps "AI" abbreviation; otherwise pass through unchanged.
  return name
    .split(' ')
    .map((word) => {
      if (word.toUpperCase() === 'AI') return 'AI'
      if (!word) return word
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}

function getPageType(pathname) {
  if (pathname.includes('_detail') || pathname.includes('/detail')) return 'Detail'
  if (pathname.includes('_create') || pathname.includes('/create')) return 'Create'
  if (pathname.includes('_profile') || pathname.includes('/profile')) return 'Profile'
  return null
}

export function Breadcrumb() {
  const location = useLocation()
  const crumbs = useMemo(() => resolveBreadcrumbs(location.pathname), [location.pathname])

  if (crumbs.length <= 1) {
    // Only Home — no need to show breadcrumbs
    return null
  }

  return (
    <nav aria-label="Breadcrumb" className="hidden md:flex items-center gap-1 text-sm min-w-0">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1

        return (
          <React.Fragment key={index}>
            {index > 0 && (
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            {crumb.isHome ? (
              isLast ? (
                <Home className="h-4 w-4 text-foreground" />
              ) : (
                <Link to={crumb.to} className="text-muted-foreground hover:text-foreground transition-colors">
                  <Home className="h-4 w-4" />
                </Link>
              )
            ) : isLast || !crumb.to ? (
              <span className="text-foreground font-medium truncate">{crumb.label}</span>
            ) : (
              <Link
                to={crumb.to}
                className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              >
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        )
      })}
    </nav>
  )
}

export default Breadcrumb
